import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Plus, Edit2, Trash2, AlertCircle, Search, Filter, XCircle } from 'lucide-react';
import { useClientStore } from '../../../core/services/ClientStore';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { IClient } from '../../../core/interfaces/IUser';
import { Card, CardContent } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../shared/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../shared/components/ui/dialog';

const serviceContainer = ServiceContainer.getInstance();
const clientService = serviceContainer.getClientService();

export const ClientsPage: React.FC = () => {
  const { clients, loading, error, setClients, addClient, updateClient, removeClient, setLoading, setError } = useClientStore();
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<IClient | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<IClient | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clientService.getAllClients();
      setClients(data);
      setError(null);
    } catch {
      setError('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [setClients, setError, setLoading]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleEdit = (client: IClient) => {
    setEditingClient(client);
    setFormData({ name: client.name, description: client.description || '' });
    setShowForm(true);
  };

  const handleDelete = (client: IClient) => {
    setClientToDelete(client);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete || deleteConfirmText !== 'DELETAR') return;

    try {
      await clientService.deleteClient(clientToDelete.id);
      removeClient(clientToDelete.id);
      setShowDeleteModal(false);
      setClientToDelete(null);
      setDeleteConfirmText('');
    } catch {
      setError('Erro ao excluir cliente');
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setClientToDelete(null);
    setDeleteConfirmText('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      if (editingClient) {
        const updated = await clientService.updateClient(editingClient.id, formData);
        updateClient(editingClient.id, updated);
      } else {
        const created = await clientService.createClient(formData);
        addClient(created);
      }
      setShowForm(false);
      setEditingClient(null);
      setFormData({ name: '', description: '' });
      setError(null);
    } catch {
      setError('Erro ao salvar cliente');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClient(null);
    setFormData({ name: '', description: '' });
  };

  const clearFilters = () => {
    setSearchTerm('');
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch = !searchTerm ||
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.description && client.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="h-8 w-8 text-blue-600" />
            Gerenciamento de Clientes
          </h1>
          <p className="text-slate-600 mt-2">Gerencie os clientes do sistema</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="h-11">
          <Plus className="h-5 w-5 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Alertas/Erros */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>✕</Button>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
          </div>
          {searchTerm && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="searchTerm" className="text-sm font-medium text-slate-700">
            Pesquisar
          </Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              id="searchTerm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome ou descrição..."
              className="h-11 pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Tabela de Clientes */}
      <Card>
        <CardContent className="p-6">
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">
                {searchTerm ? 'Nenhum cliente encontrado com os filtros aplicados' : 'Nenhum cliente cadastrado'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border max-h-[62vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Descrição</TableHead>
                    <TableHead className="text-right font-semibold">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell className="text-slate-600">{client.description || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(client)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(client)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingClient ? (
                <>
                  <Edit2 className="h-5 w-5 text-blue-600" />
                  Editar Cliente
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-blue-600" />
                  Novo Cliente
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Nome *
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome do Cliente"
                  className="h-11 pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                Descrição
              </Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição (opcional)"
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={handleCloseForm} className="h-11">
                Cancelar
              </Button>
              <Button type="submit" className="h-11">
                {editingClient ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle className="text-xl">Deletar Cliente</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ Esta ação é irreversível!
              </p>
              <p className="text-sm text-red-700">
                Você está prestes a deletar o cliente:{' '}
                <span className="font-semibold">{clientToDelete?.name}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-delete" className="text-sm font-medium text-slate-700">
                Para confirmar, digite <span className="font-bold text-red-600">DELETAR</span>:
              </Label>
              <Input
                id="confirm-delete"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Digite DELETAR"
                className="h-11"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={deleteConfirmText !== 'DELETAR' || loading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deletando...' : 'Deletar Cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
